import type { FoundryDecisionEngineRecord } from "../api/foundryV2Client";

type Props = {
  decisionEngine: FoundryDecisionEngineRecord | null;
  isLoading: boolean;
};

function toneForDomain(domainFamily: string): string {
  switch (domainFamily) {
    case "financial_erp":
      return "border-emerald-200 bg-emerald-50 text-emerald-700";
    case "industrial_erp":
      return "border-amber-200 bg-amber-50 text-amber-700";
    case "healthcare_crm":
      return "border-rose-200 bg-rose-50 text-rose-700";
    case "commercial_crm":
      return "border-cyan-200 bg-cyan-50 text-cyan-700";
    case "operational_app":
      return "border-sky-200 bg-sky-50 text-sky-700";
    default:
      return "border-slate-200 bg-slate-50 text-slate-700";
  }
}

export function FoundryDomainProfilePanel({ decisionEngine, isLoading }: Props): JSX.Element | null {
  const profile = decisionEngine?.domainProfile;
  const rationale = profile?.rationale ?? [];
  const mandatoryControls = profile?.mandatoryControls ?? [];
  const governanceBrickIds = profile?.governanceBrickIds ?? [];
  const keyQuestions = profile?.keyQuestions ?? [];
  const stackOptions = profile?.stackOptions ?? [];
  const requiredBrickIds = profile?.requiredBrickIds ?? [];
  const proofScenarios = profile?.proofScenarios ?? [];

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-[linear-gradient(135deg,#ffffff_0%,#f8fafc_55%,#eef4ff_100%)] p-5 shadow-[0_18px_50px_rgba(15,23,42,0.08)]"
      data-testid="foundry-domain-profile-panel"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-3xl">
          <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Foundry domain profile</div>
          {profile ? (
            <>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <h3 className="text-xl font-semibold text-slate-950">{profile.title}</h3>
                <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${toneForDomain(profile.domainFamily)}`}>
                  {profile.domainFamily.replace(/_/g, " ")}
                </span>
                <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                  {profile.operatingModel.replace(/_/g, " ")}
                </span>
              </div>
              <p className="mt-2 text-sm leading-6 text-slate-600">{profile.summary}</p>
            </>
          ) : (
            <>
              <h3 className="mt-2 text-xl font-semibold text-slate-950">
                {isLoading ? "Loading domain posture..." : "Domain posture not published yet"}
              </h3>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                {isLoading
                  ? "Foundry is regenerating the differentiated domain posture for this project."
                  : "Foundry has not published a differentiated domain posture for this project yet. Keep the panel visible so operators can see that the build-ready posture is still incomplete."}
              </p>
            </>
          )}
        </div>
        {profile ? (
          <div className="rounded-2xl border border-white/70 bg-white/85 p-4 text-sm text-slate-700 shadow-[0_10px_30px_rgba(15,23,42,0.06)]">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Archetype</div>
            <div className="mt-2 font-semibold text-slate-900">{profile.archetypeId}</div>
            <div className="mt-3 text-xs leading-5 text-slate-600">
              This is the operating posture Foundry will inject into the frozen contract before Jarvis starts.
            </div>
          </div>
        ) : null}
      </div>

      {profile ? (
        <div className="mt-5 grid gap-4 xl:grid-cols-[1.05fr_1.2fr]">
          <div className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Why this profile</div>
              <ul className="mt-3 space-y-2 text-sm text-slate-700">
                {rationale.map((item) => (
                  <li key={item} className="rounded-xl border border-slate-100 bg-slate-50/80 px-3 py-2">
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Mandatory controls</div>
                {mandatoryControls.length ? (
                  <ul className="mt-3 space-y-2 text-sm text-slate-700">
                    {mandatoryControls.map((item) => (
                      <li key={item} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                        {item}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="mt-3 rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">
                    No mandatory controls have been recorded yet.
                  </div>
                )}
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Governance runtime bricks</div>
                {governanceBrickIds.length ? (
                  <ul className="mt-3 space-y-2 text-sm text-slate-700">
                    {governanceBrickIds.map((item) => (
                      <li key={item} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                        {item}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="mt-3 rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">
                    Governance runtime carry-over has not been refreshed yet for this profile.
                  </div>
                )}
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Open questions</div>
                {keyQuestions.length ? (
                  <ul className="mt-3 space-y-2 text-sm text-slate-700">
                    {keyQuestions.map((item) => (
                      <li key={item} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                        {item}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="mt-3 rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">
                    No domain questions are open right now.
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Recommended stacks</div>
              {stackOptions.length ? (
                <div className="mt-3 grid gap-3">
                  {stackOptions.map((option) => (
                    <article
                      key={option.id}
                      className={`rounded-2xl border p-4 ${
                        option.recommended ? "border-blue-200 bg-blue-50/80" : "border-slate-200 bg-slate-50/80"
                      }`}
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="text-sm font-semibold text-slate-900">{option.label}</div>
                        {option.recommended ? (
                          <span className="rounded-full border border-blue-200 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700">
                            AI recommended
                          </span>
                        ) : null}
                        <button
                          type="button"
                          className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-slate-200 bg-white text-[11px] font-semibold text-slate-600"
                          title={[...option.why, ...option.suitableWhen, ...option.caution].join("\n")}
                          aria-label={`Why Foundry recommends ${option.label}`}
                        >
                          ?
                        </button>
                      </div>
                      <div className="mt-2 text-sm text-slate-700">{option.summary}</div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {option.stack.map((entry) => (
                          <span key={entry} className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] text-slate-700">
                            {entry}
                          </span>
                        ))}
                      </div>
                      {option.caution.length ? (
                        <div className="mt-3 text-xs leading-5 text-slate-600">
                          Caution: {option.caution.join(" ")}
                        </div>
                      ) : null}
                    </article>
                  ))}
                </div>
              ) : (
                <div className="mt-3 rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">
                  Foundry has not published a differentiated stack recommendation for this profile yet.
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Service and proof bias</div>
              <div className="mt-3 grid gap-4 md:grid-cols-2">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Required bricks</div>
                  {requiredBrickIds.length ? (
                    <ul className="mt-2 space-y-2 text-sm text-slate-700">
                      {requiredBrickIds.map((item) => (
                        <li key={item} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                          {item}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="mt-2 rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">
                      No required bricks have been locked yet.
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Governance carry-over</div>
                  {governanceBrickIds.length ? (
                    <ul className="mt-2 space-y-2 text-sm text-slate-700">
                      {governanceBrickIds.slice(0, 4).map((item) => (
                        <li key={item} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                          {item}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="mt-2 rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">
                      Governance carry-over will appear once the profile is refreshed under the phase 3 contract.
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Proof scenarios</div>
                  {proofScenarios.length ? (
                    <ul className="mt-2 space-y-2 text-sm text-slate-700">
                      {proofScenarios.map((item) => (
                        <li key={item} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                          {item}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="mt-2 rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">
                      Proof scenarios will appear once the domain profile is regenerated.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          {isLoading
            ? "Loading differentiated domain posture..."
            : "No differentiated domain posture has been published yet. The panel stays visible so the missing posture is explicit before Jarvis handoff."}
        </div>
      )}
    </section>
  );
}
